// src/utils/walletUtils.js

/**
 * Get wallet provider based on wallet type
 * @param {string} walletType - The type of wallet
 * @param {string} preferredChain - Optional preferred chain for multi-chain wallets
 * @returns {Object|null} Wallet provider or null if not available
 */
export function getWalletProvider(walletType, preferredChain = null) {
  if (!walletType) return null;
  
  // Handle case where walletType is an object instead of a string
  let type = '';
  if (typeof walletType === 'string') {
    type = walletType.toLowerCase();
  } else if (walletType && walletType.type && typeof walletType.type === 'string') {
    // If walletType is an object with a type property, use that
    type = walletType.type.toLowerCase();
  } else {
    console.warn('Invalid wallet type:', walletType);
    return null;
  }
  
  try {
    // OKX wallet special handling for Solana
    if (type === 'okx' && preferredChain === 'solana') {
      return getOkxSolanaProvider();
    }
  
    // OKX wallet (Ethereum/EVM)
    if (type === 'okx' && window.okxwallet) {
      return window.okxwallet;
    }
    
    // MetaMask and other injected EVM wallets
    if ((type === 'metamask' || type === 'injected') && window.ethereum) {
      return window.ethereum;
    }
    
    // Binance Chain Wallet
    if (type === 'binance' && window.BinanceChain) {
      return window.BinanceChain;
    }
    
    // Trust Wallet
    if (type === 'trust' && window.trustwallet) {
      return window.trustwallet;
    }
    
    // Coinbase Wallet
    if (type === 'coinbase' && window.coinbaseWalletExtension) {
      return window.coinbaseWalletExtension;
    }
    
    // Phantom Wallet (Solana) - IMPROVED ERROR HANDLING
    if (type === 'phantom') {
      // Check if phantom object exists first
      if (!window.phantom) {
        console.warn('Phantom wallet not detected in window.phantom');
        return null;
      }
      
      // Check if solana provider exists
      if (!window.phantom.solana) {
        console.warn('Phantom solana provider not found');
        return null;
      }
      
      // Verify that it looks like a proper phantom provider
      const provider = window.phantom.solana;
      if (!provider || typeof provider.connect !== 'function') {
        console.warn('Phantom provider missing connect method');
        return null;
      }
      
      return provider;
    }
    
    // Solflare Wallet (Solana)
    if (type === 'solflare' && window.solflare) {
      return window.solflare;
    }
    
    // Ledger (via Ledger Live)
    if (type === 'ledger' && window.ledgerLive) {
      return window.ledgerLive;
    }
    
    // WalletConnect
    if (type === 'walletconnect' && window.walletConnectProvider) {
      return window.walletConnectProvider;
    }
    
    // Generic fallback - try to find a provider matching the wallet name
    if (window[type]) {
      return window[type];
    }
    
    // Check if ethereum is actually the requested wallet (common for injected wallets)
    if (window.ethereum) {
      // Check ethereum wallet name/id for match
      if (window.ethereum.isMetaMask && type === 'metamask') {
        return window.ethereum;
      }
      if (window.ethereum.isTrust && type === 'trust') {
        return window.ethereum;
      }
      if (window.ethereum.isCoinbaseWallet && type === 'coinbase') {
        return window.ethereum;
      }
      if (window.ethereum.isOkxWallet && type === 'okx') {
        return window.ethereum;
      }
      
      // Last resort for EVM wallets - return ethereum if we need an EVM wallet
      if (preferredChain !== 'solana' && 
         (type === 'metamask' || type === 'trust' || type === 'coinbase' || type === 'binance')) {
        return window.ethereum;
      }
    }
    
    return null;
  } catch (error) {
    console.error('Error getting wallet provider:', error);
    return null;
  }
}

/**
 * Get the OKX Solana provider with proper error handling
 * @returns {Object|null} The OKX Solana provider or null if not available
 */
export function getOkxSolanaProvider() {
  try {
    // Check if OKX wallet extension exists
    if (!window.okxwallet) {
      console.log('OKX wallet extension not detected');
      return null;
    }
    
    // Log available OKX wallet properties for debugging
    console.log('OKX wallet properties:', Object.keys(window.okxwallet));
    
    // Check if Solana provider exists in OKX wallet
    if (!window.okxwallet.solana) {
      console.log('OKX Solana provider not available - Solana might not be enabled in wallet settings');
      // Check if there's any property related to Solana with different casing
      const solanaProps = Object.keys(window.okxwallet).filter(
        key => key.toLowerCase().includes('solana')
      );
      if (solanaProps.length > 0) {
        console.log('Found potential Solana-related properties:', solanaProps);
      }
      return null;
    }
    
    console.log('OKX Solana provider found:', typeof window.okxwallet.solana);
    return window.okxwallet.solana;
  } catch (error) {
    console.error('Error accessing OKX Solana provider:', error);
    return null;
  }
}

/**
 * Get chain information from provider
 * @param {Object} provider - Wallet provider
 * @param {string} walletType - Wallet type
 * @param {string} preferredChain - Optional preferred chain for multi-chain wallets
 * @returns {Object} Chain information
 */
export async function getChainInfo(provider, walletType, preferredChain = null) {
  try {
    // Handle case when walletType is an object
    let typeStr = '';
    if (typeof walletType === 'string') {
      typeStr = walletType;
    } else if (walletType && walletType.type && typeof walletType.type === 'string') {
      typeStr = walletType.type;
    } else {
      typeStr = 'unknown';
    }
    
    // Handle Solana providers
    if (preferredChain === 'solana' || isSolanaWallet(typeStr)) {
      return {
        chainId: 'solana',
        name: 'Solana',
        network: 'solana',
        balance: '0.00',  // You would need to implement balance fetching for Solana
        usdtBalance: '0.00'
      };
    }
    
    // Handle EVM providers
    if (provider && provider.request) {
      // Get current chain ID
      const chainId = await provider.request({ method: 'eth_chainId' });
      
      // Normalize for common networks
      let chainName = '';
      let network = '';
      
      // Map chainId to network name
      if (chainId === '0x1') {
        chainName = 'Ethereum Mainnet';
        network = 'ethereum';
      } else if (chainId === '0x38') {
        chainName = 'BNB Smart Chain';
        network = 'bsc';
      } else if (chainId === '0x89') {
        chainName = 'Polygon';
        network = 'polygon';
      } else {
        chainName = `Chain ID: ${parseInt(chainId, 16)}`;
        network = 'unknown';
      }
      
      // You would implement balance fetching here in a real app
      const balance = '0.00';
      const usdtBalance = '0.00';
      
      return {
        chainId,
        name: chainName,
        network,
        balance,
        usdtBalance
      };
    }
    
    // Default fallback
    return {
      chainId: null,
      name: 'Unknown Network',
      network: 'unknown',
      balance: '0.00',
      usdtBalance: '0.00'
    };
  } catch (error) {
    console.error('Error getting chain info:', error);
    
    // Return default values on error
    return {
      chainId: null,
      name: 'Unknown Network',
      network: 'unknown',
      balance: '0.00',
      usdtBalance: '0.00'
    };
  }
}

/**
 * Check if a wallet type or address is a Solana wallet
 * @param {string} addressOrType - The wallet address or wallet type to check
 * @returns {boolean} True if it's a Solana wallet
 */
export function isSolanaWallet(addressOrType) {
  if (!addressOrType || typeof addressOrType !== 'string') {
    return false;
  }
  
  // Check if it's a wallet type first
  const solanaWalletTypes = ['phantom', 'solflare', 'sollet'];
  if (solanaWalletTypes.includes(addressOrType.toLowerCase())) {
    return true;
  }
  
  // For OKX wallet, we can't determine just by the type
  if (addressOrType.toLowerCase() === 'okx') {
    return false; // We'll handle this separately in connection code
  }
  
  // If it's an address (not a type), check the format
  // Solana addresses are base58 encoded and typically 32-44 characters long
  // They don't start with 0x (unlike Ethereum addresses)
  return !addressOrType.startsWith('0x') && addressOrType.length >= 32 && addressOrType.length <= 44;
}

/**
 * Detect available wallet providers in the browser
 * @returns {Object} Object with available wallet providers
 */
export function detectWalletProviders() {
  const wallets = {
    okx: false,
    binance: false,
    metamask: false,
    trust: false,
    coinbase: false,
    phantom: false,
    solflare: false,
    ledger: false,
    walletconnect: false,
    other: false
  };
  
  try {
    // Detect OKX wallet
    if (window.okxwallet) {
      wallets.okx = true;
    }
    
    // Detect MetaMask
    if (window.ethereum?.isMetaMask) {
      wallets.metamask = true;
    }
    
    // Detect Binance Chain Wallet
    if (window.BinanceChain) {
      wallets.binance = true;
    }
    
    // Detect Trust Wallet
    if (window.ethereum?.isTrust || window.trustwallet) {
      wallets.trust = true;
    }
    
    // Detect Coinbase Wallet
    if (window.ethereum?.isCoinbaseWallet || window.coinbaseWalletExtension) {
      wallets.coinbase = true;
    }
    
    // Detect Phantom
    if (window.phantom?.solana) {
      wallets.phantom = true;
    }
    
    // Detect Solflare
    if (window.solflare) {
      wallets.solflare = true;
    }
    
    // Detect Ledger Live
    if (window.ledgerLive) {
      wallets.ledger = true;
    }
    
    // Detect WalletConnect
    if (window.walletConnectProvider) {
      wallets.walletconnect = true;
    }
    
    // Detect other Ethereum wallets
    if (window.ethereum && !wallets.metamask && !wallets.trust && !wallets.coinbase && !wallets.okx) {
      wallets.other = true;
    }
  } catch (error) {
    console.error('Error detecting wallet providers:', error);
  }
  
  return wallets;
}

/**
 * Get wallet support information for display
 * @param {string|Object} walletType - The wallet type
 * @returns {Object} Support info object
 */
export function getWalletSupportInfo(walletType) {
  // Handle case when walletType is an object
  let type = '';
  if (typeof walletType === 'string') {
    type = walletType.toLowerCase();
  } else if (walletType && walletType.type && typeof walletType.type === 'string') {
    type = walletType.type.toLowerCase();
  } else {
    // Default to 'unknown' if we can't determine the type
    type = 'unknown';
  }
  
  switch (type) {
    case 'okx':
      return {
        supportsEVM: true,
        supportsSolana: true,
        supportInfo: "OKX Wallet supports both EVM chains (Ethereum, BSC) and Solana. You'll need to select which chain to use."
      };
    case 'phantom':
    case 'solflare':
      return {
        supportsEVM: false,
        supportsSolana: true,
        supportInfo: "This wallet only supports Solana connections."
      };
    case 'metamask':
    case 'trust':
    case 'coinbase':
    case 'binance':
    case 'walletconnect':
      return {
        supportsEVM: true,
        supportsSolana: false,
        supportInfo: "This wallet only supports EVM connections (Ethereum, BSC)."
      };
    default:
      return {
        supportsEVM: true,
        supportsSolana: false,
        supportInfo: "This wallet type may have limited network support."
      };
  }
}

/**
 * Sign a message using the wallet
 * @param {string|Object} walletType - The wallet type
 * @param {string} address - Account address
 * @param {string} message - Message to sign
 * @returns {Promise<string>} Signature
 */
export async function signMessage(walletType, address, message) {
  try {
    // Normalize wallet type
    let type = '';
    if (typeof walletType === 'string') {
      type = walletType.toLowerCase();
    } else if (walletType && walletType.type && typeof walletType.type === 'string') {
      type = walletType.type.toLowerCase();
    } else {
      console.warn('Invalid wallet type for signing:', walletType);
      return ''; // Return empty string instead of throwing
    }
    
    const provider = getWalletProvider(type);
    if (!provider) {
      console.warn('No provider found for wallet type:', type);
      return ''; // Return empty string instead of throwing
    }
    
    // Handle Solana wallets
    if (type === 'phantom' || type === 'solflare' || 
        (type === 'okx' && provider === window.okxwallet?.solana)) {
      
      // Encode message for Solana
      const encodedMessage = new TextEncoder().encode(message);
      
      // Sign message
      const signResult = await provider.signMessage(encodedMessage, 'utf8');
      
      // Return signature in appropriate format
      return signResult.signature || signResult;
    }
    
    // Handle EVM wallets
    if (provider && provider.request) {
      // Request personal signature (standard EVM method)
      const signature = await provider.request({
        method: 'personal_sign',
        params: [message, address]
      });
      
      return signature;
    }
    
    console.warn('No signing method found for wallet type:', type);
    return ''; // Return empty string instead of throwing
  } catch (error) {
    console.error('Error signing message:', error);
    return ''; // Return empty string instead of throwing
  }
}

/**
 * Format wallet address for display
 * @param {string} address - Full wallet address
 * @param {number} start - Number of characters to show at start
 * @param {number} end - Number of characters to show at end
 * @returns {string} Formatted address (e.g., "0x1234...abcd")
 */
export function formatAddress(address, start = 6, end = 4) {
  if (!address || typeof address !== 'string') {
    return '';
  }
  
  if (address.length <= start + end + 3) {
    return address;
  }
  
  return `${address.slice(0, start)}...${address.slice(-end)}`;
}

/**
 * Check if OKX Solana provider is available
 * Helper function for UI to determine if OKX Solana is a viable option
 * @returns {boolean} True if OKX Solana is available
 */
export function isOkxSolanaAvailable() {
  return !!(window.okxwallet && window.okxwallet.solana);
}

/**
 * Connect directly to OKX Solana wallet
 * Simplified external version of the connection method
 * @returns {Promise<Object>} Connection result
 */
export async function connectOkxSolana() {
  if (!window.okxwallet || !window.okxwallet.solana) {
    throw new Error("OKX Solana provider not available");
  }
  
  try {
    const provider = window.okxwallet.solana;
    
    // First check if already connected
    if (provider.isConnected && provider.publicKey) {
      const publicKey = provider.publicKey.toString ? 
        provider.publicKey.toString() : 
        provider.publicKey;
      return { connected: true, publicKey };
    }
    
    // Try to connect
    const response = await provider.connect();
    
    // Handle null response
    if (response === null) {
      // Check if publicKey is now available on provider
      if (provider.publicKey) {
        const publicKey = provider.publicKey.toString ? 
          provider.publicKey.toString() : 
          provider.publicKey;
        return { connected: true, publicKey };
      }
      throw new Error("Could not retrieve public key");
    }
    
    // Handle response with publicKey
    if (response && response.publicKey) {
      const publicKey = response.publicKey.toString ? 
        response.publicKey.toString() : 
        response.publicKey;
      return { connected: true, publicKey };
    }
    
    throw new Error("Could not connect to OKX Solana wallet");
  } catch (error) {
    if (error.code === 4001) {
      throw new Error("User rejected the connection request");
    }
    throw error;
  }
}
